<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { getWorkspace, deleteRole } from '@/app/core/services/roles'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTable,
  DModal,
  DLoading,
  DTextField,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon
} from 'front-library'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref('')
const RoleForDelete = ref()

/* Datos componente table */
const columns = ref([
  { name: 'code', title: t('roles.list.code') },
  { name: 'name', title: t('roles.list.name') },
  { name: 'actions', title: '' }
])
const rows = ref()
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const tableRoles = ref(null)
const sortBy = ref('')

/* Redirect's */
const createRole = () => {
  void router.push({ name: 'CreateRole' })
}
const updateRole = (id: any) => {
  void router.push({ name: 'UpdateRole', params: { id } })
}
const getRole = (id: any) => {
  void router.push({ name: 'GetRole', params: { id } })
}

/* Eliminar rol */
const removeRole = (values: any) => {
  typeAlertModal.value = 'question'
  titleModal.value = t('roles.list.actionStatus', {
    rol: values.name as string
  })
  descriptionModal.value = t('modal.description')
  showModal.value = true
  RoleForDelete.value = values.id
}
const deleteConfirm = async () => {
  loading.value = true
  showModal.value = false
  await deleteRole(RoleForDelete.value)
    .then((response: any) => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('roles.notification.action', [t('notification.wasRemoved')]),
        type: 'success',
        time: 10000
      })
      void roles()
    })
    .catch((error: any) => {
      notificationError(error, 'roles', t, notifications, loading)
    })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Listar roles */
const roles = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const items: any = await getWorkspace(store.user?.workspaceId)
    rows.value = items.assignedRoles
    if (search.value !== '')
      rows.value = rows.value.filter(function (obj: any) {
        const searchLower = search.value.toLowerCase()
        const nameLower = obj.name.toLowerCase()
        const codeLower = obj.code.toLowerCase()
        return (
          codeLower.includes(searchLower) || nameLower.includes(searchLower)
        )
      })
    totalRows.value = items.assignedRoles.length
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'roles', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="
      t('modal.acceptQuestion', {
        action: t('table.action.delete')
      })
    "
    @closeModal="closeModal"
    @otherMethod="deleteConfirm" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('roles.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('roles.list.description')" />
      </div>
      <!-- justify-between -->
      <div class="sm:flex sm:justify-between items-center pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="roles" />
          <DBtn @click.prevent="roles" v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createRole()"
          v-if="store.hasPermission('role:create')">
          {{
            t('form.button.create', {
              form: t('roles.list.title', 0)
            })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableRoles"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :per-page-dropdown="[200]"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), roles()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), roles()">
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('role:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div class="bg-gray-50 rounded-md py-2" @click="getRole(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('role:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateRole(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('role:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div class="bg-gray-50 rounded-md py-2" @click="removeRole(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
